import { PlayerTransport } from "@/features/playback/PlayerTransport";
import { Cast, Inbox } from "lucide-react";
import { useRef } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  getAppSettings,
  getLiveStatusForTeam,
  getPublicTeam,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { publicAppSettingsKey, useViewerHostKey } from "@/lib/queryClient";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useMe } from "@/features/auth/useMe";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useChromecast } from "@/features/playback/useChromecast";

/**
 * `/teams/$teamSlug/live` — per-team public live viewer (M12c).
 *
 * Mirrors the M11b global `LiveShell` flow but reads the per-team status
 * (`/api/live/status?team_slug=<slug>`) and points `<HlsVideo>` at the
 * per-team HLS proxy (`/api/live/hls/teams/<slug>/...`). The team-slug
 * is in the URL; the stream key never is.
 *
 * Public teams are anonymous-readable; authorized members/operators and
 * claimed guests can also open private teams. Unauthorized private-team
 * lookups and status reads return the same 404 shape. The CTA on
 * `/teams/$teamSlug` routes here instead of the legacy global `/live`.
 */
export function TeamLivePage() {
  const { teamSlug } = useParams({ from: "/teams/$teamSlug/live" });
  const search = useSearch({ from: "/teams/$teamSlug/live" });
  const meQuery = useMe();
  const authProbePending = meQuery.isPending && !meQuery.isFetched;
  const teamQueryKey = useViewerHostKey("public-team", { slug: teamSlug });
  const statusQueryKey = useViewerHostKey("team-live-status", {
    slug: teamSlug,
  });

  const teamQuery = useQuery({
    queryKey: teamQueryKey,
    queryFn: () => getPublicTeam(teamSlug),
    enabled: !authProbePending,
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && [403, 404].includes(err.status))
        return false;
      return failureCount < 2;
    },
  });

  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 5 * 60_000,
  });
  const settings = settingsQuery.data?.settings;
  const settingsLiveEnabled = settings
    ? coerceBool(settings.live_enabled)
    : true;

  const statusQuery = useQuery({
    queryKey: statusQueryKey,
    queryFn: () => getLiveStatusForTeam(teamSlug),
    enabled: !authProbePending && settingsLiveEnabled && !!teamQuery.data,
    refetchInterval: settingsLiveEnabled ? 4_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  const status = statusQuery.data;

  const team = teamQuery.data;
  useDocumentMeta({
    title: team ? `Live · ${team.name}` : "Live · tktic",
    description: team ? `Live broadcast from ${team.name}.` : undefined,
    ogType: "video.other",
  });

  if (authProbePending || teamQuery.isLoading) {
    return (
      <main data-testid="team-live" className="min-h-[40vh] bg-canvas text-ink">
        <div className="mx-auto max-w-5xl px-6 py-10">
          <p
            data-testid="team-live-loading"
            className="font-mono text-sm text-ink-muted"
          >
            Loading team…
          </p>
        </div>
      </main>
    );
  }

  if (teamQuery.isError) {
    const status =
      teamQuery.error instanceof ApiError ? teamQuery.error.status : null;
    const notFound = status === 404;
    const denied = status === 403;
    return (
      <main
        data-testid="team-live"
        className="team-unavailable min-h-[40vh] bg-canvas text-ink"
      >
        <header>
          <p className="eyebrow">Team access</p>
          <h1
            data-testid={
              notFound
                ? "team-live-404"
                : denied
                  ? "team-live-denied"
                  : "team-live-error"
            }
          >
            {notFound
              ? "Team not found."
              : denied
                ? "This team isn’t available to your account."
                : "We couldn’t load this right now."}
          </h1>
        </header>
        <section
          className="scorebook-data-state"
          role={denied ? undefined : "alert"}
        >
          <span className="scorebook-state-icon" aria-hidden>
            <Inbox />
          </span>
          <h2>
            {denied || notFound ? "Find your team." : "Try again in a moment."}
          </h2>
          <p>
            {denied || notFound
              ? "Return to the club directory to see the teams available to you. Private teams require membership or an invitation from the club."
              : "Your work is still here. Refresh the page or return to the club directory."}
          </p>
          <Link
            to="/teams"
            data-testid="team-live-back"
            className="td-empty-secondary"
          >
            Browse teams
          </Link>
        </section>
      </main>
    );
  }

  if (!team) return null;

  const isDisabled = !settingsLiveEnabled || status?.enabled === false;
  const isActive = !isDisabled && !!(status?.active && status?.ready);
  const offlineMessage =
    settings?.live_offline_message || "No live stream right now.";

  return (
    <main
      data-testid="team-live"
      data-team-slug={team.slug}
      data-live-state={isDisabled ? "disabled" : isActive ? "live" : "offline"}
      className="min-h-screen bg-canvas text-ink"
    >
      <header className="mx-auto flex max-w-7xl flex-col items-stretch justify-between gap-3 border-b border-rule px-4 py-3 sm:px-6 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/teams/$teamSlug"
            params={{ teamSlug: team.slug }}
            search={{ season: search.season, view: "overview" }}
            data-testid="team-live-breadcrumb"
            className="font-mono text-xs uppercase tracking-mono text-ink-muted hover:text-accent"
          >
            ← {team.name}
          </Link>

          <StatusPill
            state={isDisabled ? "disabled" : isActive ? "live" : "offline"}
          />
        </div>
      </header>

      <div className="team-live-heading">
        <p className="eyebrow">Match day</p>
        <h1>{team.name}, live.</h1>
        <p className="text-ink-muted">Follow the action with your team.</p>
      </div>
      <section className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-5 sm:px-6 sm:py-6">
        {isDisabled ? (
          <DisabledPlaceholder />
        ) : isActive ? (
          <TeamLivePlayer team={team} />
        ) : (
          <OfflinePlaceholder
            message={offlineMessage}
            teamSlug={team.slug}
            seasonId={search.season}
          />
        )}
      </section>
      <footer className="team-live-footer">
        <span>{team.name} · Team stream</span>
        <Link
          to="/teams/$teamSlug"
          params={{ teamSlug: team.slug }}
          search={{ season: search.season, view: "matches" }}
        >
          Browse match recordings
        </Link>
      </footer>
    </main>
  );
}

function StatusPill({ state }: { state: "disabled" | "live" | "offline" }) {
  if (state === "live") {
    return (
      <span
        data-testid="team-live-status-pill"
        data-state="live"
        className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-paper"
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-paper animate-pulse"
          aria-hidden="true"
        />
        LIVE
      </span>
    );
  }
  if (state === "disabled") {
    return (
      <span
        data-testid="team-live-status-pill"
        data-state="disabled"
        className="inline-flex items-center rounded-full border border-rule bg-canvas px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
      >
        Disabled
      </span>
    );
  }
  return (
    <span
      data-testid="team-live-status-pill"
      data-state="offline"
      className="inline-flex items-center rounded-full border border-rule bg-canvas px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
    >
      Offline
    </span>
  );
}

function TeamLivePlayer({ team }: { team: { slug: string; name: string } }) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsUrl = `/api/live/hls/teams/${encodeURIComponent(team.slug)}/index.m3u8`;
  const chromecast = useChromecast(videoRef, () => ({
    url: hlsUrl,
    contentType: "application/x-mpegURL",
    streamType: "LIVE",
    title: `${team.name} · live`,
    subtitle: "Live now",
  }));

  return (
    <div
      ref={frameRef}
      data-testid="team-live-player"
      className="relative flex flex-col gap-2"
    >
      <HlsVideo
        ref={videoRef}
        hlsUrl={hlsUrl}
        mp4Url=""
        live
        autoPlay
        controls={false}
        className="aspect-video w-full rounded bg-black"
        data-testid="team-live-video"
      />
      <PlayerTransport
        videoRef={videoRef}
        frameRef={frameRef}
        live
        casting={
          chromecast.supportedBrowser ? (
            <button
              type="button"
              onClick={chromecast.toggle}
              disabled={!chromecast.sdkReady}
              title={chromecast.unavailableReason || undefined}
              data-testid="team-live-chromecast-btn"
              data-casting={chromecast.casting ? "true" : undefined}
              aria-label={
                chromecast.casting
                  ? `Stop casting to ${chromecast.deviceName || "screen"}`
                  : "Cast to a screen"
              }
            >
              <Cast aria-hidden />
            </button>
          ) : null
        }
      />
      <p className="live-source-caption">
        Streaming from the {team.name} sideline.
      </p>
    </div>
  );
}

function DisabledPlaceholder() {
  return (
    <div
      data-testid="team-live-disabled-placeholder"
      className="flex aspect-video flex-col items-center justify-center gap-2 rounded border border-rule bg-canvas p-6 text-center text-ink-muted"
    >
      <p className="font-display text-sm uppercase tracking-wider">
        Live streaming is disabled
      </p>
      <p className="font-mono text-xs">
        An admin has turned off live streaming in app settings.
      </p>
    </div>
  );
}

function OfflinePlaceholder({
  message,
  teamSlug,
  seasonId,
}: {
  message: string;
  teamSlug: string;
  seasonId?: string;
}) {
  return (
    <div
      data-testid="team-live-offline-placeholder"
      className="flex aspect-video flex-col items-center justify-center gap-2 rounded border border-rule bg-canvas p-6 text-center text-ink-muted"
    >
      <span className="scorebook-state-icon" aria-hidden>
        <Inbox />
      </span>
      <h2>The team isn’t live right now.</h2>
      <p className="team-live-offline-copy">
        {message} Recordings from earlier matches are in the team library.
      </p>
      <Link
        to="/teams/$teamSlug"
        params={{ teamSlug }}
        search={{ season: seasonId, view: "matches" }}
        className="td-empty-primary"
      >
        Explore matches
      </Link>
    </div>
  );
}
